import { apiGet, apiPost, apiPut, apiDelete } from '../lib/api-client';

/**
 * Generic CRUD service helpers for any collection.
 *
 * These functions call the API Worker REST endpoints and return data
 * in the same shape the frontend components already consume from the
 * Appwrite SDK: list → { documents, total }, get/create/update → single doc.
 */

/**
 * List documents in a collection with optional filters.
 *
 * @param collection - API route segment (e.g. 'villas', 'bookings')
 * @param filters - Optional query parameters (equality filters, limit, offset, orderBy, order)
 * @returns Object with `documents` array and `total` count
 */
export async function list<T = any>(
  collection: string,
  filters?: Record<string, string | number | boolean | undefined>
): Promise<{ documents: T[]; total: number }> {
  return apiGet<{ documents: T[]; total: number }>(`/${collection}`, filters);
}

/**
 * Get a single document by ID.
 *
 * @param collection - API route segment
 * @param id - Document ID
 * @returns The document
 */
export async function get<T = any>(collection: string, id: string): Promise<T> {
  return apiGet<T>(`/${collection}/${id}`);
}

/**
 * Create a new document in a collection.
 *
 * @param collection - API route segment
 * @param data - Document fields to create
 * @returns The created document
 */
export async function create<T = any>(
  collection: string,
  data: Record<string, any>
): Promise<T> {
  return apiPost<T>(`/${collection}`, data);
}

/**
 * Update an existing document by ID.
 *
 * @param collection - API route segment
 * @param id - Document ID
 * @param data - Fields to update
 * @returns The updated document
 */
export async function update<T = any>(
  collection: string,
  id: string,
  data: Record<string, any>
): Promise<T> {
  return apiPut<T>(`/${collection}/${id}`, data);
}

/**
 * Delete a document by ID.
 *
 * @param collection - API route segment
 * @param id - Document ID
 */
export async function remove(collection: string, id: string): Promise<void> {
  return apiDelete<void>(`/${collection}/${id}`);
}
